import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { CLINIC, whatsappLink } from "@/lib/clinic";

const navItems = [
    { label: "About", id: "about" },
    { label: "Services", id: "services" },
    { label: "Doctors", id: "doctors" },
    { label: "Gallery", id: "gallery" },
    { label: "FAQ", id: "faq" },
    { label: "Contact", id: "contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        setOpen(false);
    };

    return (
        <motion.header
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/80 backdrop-blur-xl border-b border-[#1A221F]/5 shadow-[0_2px_20px_rgba(0,0,0,0.04)]"
                    : "bg-transparent"
            }`}
            data-testid="navbar"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
                <button
                    onClick={() => scrollTo("hero")}
                    className="flex items-center gap-2 group"
                    data-testid="nav-logo"
                >
                    <div className="w-10 h-10 rounded-full bg-[#3B5249] flex items-center justify-center text-white font-display font-semibold">
                        M
                    </div>
                    <div className="flex flex-col items-start leading-tight">
                        <span className="font-display text-lg font-semibold text-[#1A221F]">
                            Mirrari
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#7A8C84]">
                            Clinic
                        </span>
                    </div>
                </button>

                <nav className="hidden lg:flex items-center gap-8">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => scrollTo(item.id)}
                            className="text-sm text-[#4A5D54] hover:text-[#3B5249] transition-colors relative group"
                            data-testid={`nav-${item.id}-link`}
                        >
                            {item.label}
                            <span className="absolute -bottom-1 left-0 w-0 h-[1.5px] bg-[#3B5249] group-hover:w-full transition-all duration-300" />
                        </button>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href={`tel:${CLINIC.phoneRaw}`}
                        className="flex items-center gap-2 text-sm text-[#3B5249] hover:text-[#2C3E37] transition-colors"
                        data-testid="nav-call-link"
                    >
                        <Phone className="w-4 h-4" />
                        {CLINIC.phone}
                    </a>
                    <a
                        href={whatsappLink()}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-full bg-[#3B5249] text-white px-5 py-2.5 text-sm hover:bg-[#2C3E37] transition-all hover:scale-105"
                        data-testid="nav-book-button"
                    >
                        Book Appointment
                    </a>
                </div>

                <button
                    onClick={() => setOpen(!open)}
                    className="lg:hidden w-10 h-10 rounded-full bg-[#F3F1EC] flex items-center justify-center text-[#3B5249]"
                    data-testid="nav-mobile-toggle"
                    aria-label="Menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="lg:hidden bg-white border-t border-[#E8E6E1] overflow-hidden"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {navItems.map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => scrollTo(item.id)}
                                    className="text-left text-[#1A221F] font-display text-lg"
                                    data-testid={`nav-mobile-${item.id}-link`}
                                >
                                    {item.label}
                                </button>
                            ))}
                            <div className="pt-4 flex flex-col gap-3 border-t border-[#E8E6E1]">
                                <a
                                    href={`tel:${CLINIC.phoneRaw}`}
                                    className="flex items-center gap-2 text-[#3B5249]"
                                    data-testid="nav-mobile-call-link"
                                >
                                    <Phone className="w-4 h-4" />
                                    {CLINIC.phone}
                                </a>
                                <a
                                    href={whatsappLink()}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="rounded-full bg-[#3B5249] text-white px-5 py-3 text-sm text-center"
                                    data-testid="nav-mobile-book-button"
                                >
                                    Book Appointment
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
